import React, { useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ChevronRight,
  DotsVertical,
  Edit01,
  Archive,
  Menu,
  Plus,
  Table,
} from "@opendate-app/setlist";
import { errorText, toastError, toastSuccess } from "../../shared/toastHelper";
import { formatPrice } from "../../shared/teamCurrencyFormatting";
import DetailPageLayout, { DetailColumns } from "../ClassesShared/DetailPageLayout";
import ArchiveConfirmModal from "../ClassesShared/ArchiveConfirmModal";
import ClassCategoryBadge from "../ClassesShared/ClassCategoryBadge";
import ClassFormatBadge from "../ClassesShared/ClassFormatBadge";
import ClassStatusBadge from "../ClassesShared/ClassStatusBadge";
import { formatDateRange } from "../ClassesShared/classLabels";
import { sessionLengthLabel, templateLength } from "../ClassesShared/templateLength";
import DetailField from "../ClassInstances/registrationPage/DetailField";
import TemplateSectionsEditor from "../ClassesShared/TemplateSectionsEditor";
import { sectionsAttributes, sectionsError, sectionsFromTemplate } from "../ClassesShared/templateSections";
import "./classTemplates.css";
import "../ClassesShared/classesShared.css";

export function templateMeta(classTemplate, currencyCode) {
  return [
    "Template",
    templateLength(classTemplate.sessions_count, classTemplate.default_session_duration_minutes),
    classTemplate.default_price != null ? `${formatPrice(Number(classTemplate.default_price), currencyCode)} default` : null,
    classTemplate.default_capacity ? `capacity ${classTemplate.default_capacity}` : null,
  ].filter(Boolean).join(" · ");
}

function InstancesCard({ classInstances, totalCount, classesPath, newInstancePath }) {
  const columns = [
    {
      key: "name",
      header: "Class",
      primary: true,
      render: (instance) => (
        <a href={`${classesPath}/${instance.id}`} className="text-reset text-decoration-none">{instance.name}</a>
      ),
    },
    { key: "dates", header: "Date range", render: (instance) => formatDateRange(instance.starts_on, instance.ends_on, instance.time_zone) },
    {
      key: "capacity",
      header: "Capacity",
      render: (instance) => `${instance.status === "canceled" ? "—" : instance.spots_taken ?? 0} / ${instance.capacity ?? "—"}`,
    },
    { key: "status", header: "Status", render: (instance) => <ClassStatusBadge status={instance.status} /> },
    {
      key: "open",
      header: <span className="sr-only">Actions</span>,
      align: "right",
      render: (instance) => (
        <Button as="a" variant="tertiary" size="sm" icon={ChevronRight} href={`${classesPath}/${instance.id}`} aria-label={`Open ${instance.name}`} />
      ),
    },
  ];

  return (
    <Card
      padding="none"
      header={(
        <CardHeader
          title="Classes Scheduled"
          supportingText={totalCount > classInstances.length
            ? `The ${classInstances.length} most recent of ${totalCount} classes created from this template.`
            : "Every class created from this template."}
          actions={newInstancePath && (
            <Button as="a" variant="secondary" icon={Plus} href={newInstancePath}>New Class</Button>
          )}
        />
      )}
    >
      <Table className="classes-table"
        columns={columns}
        data={classInstances}
        getRowKey={(instance) => instance.id}
        stackBelow={400}
        empty="No classes scheduled from this template yet."
      />
    </Card>
  );
}

InstancesCard.propTypes = {
  classInstances: PropTypes.array.isRequired,
  totalCount: PropTypes.number.isRequired,
  classesPath: PropTypes.string.isRequired,
  newInstancePath: PropTypes.string,
};

function CurriculumCard({ sessions }) {
  return (
    <Card header={<CardHeader title="Curriculum" supportingText="The sessions every class from this template starts with." />}>
      {sessions.length === 0 ? (
        <div className="classes-text-secondary">No curriculum sessions yet.</div>
      ) : (
        <ol className="class-template-curriculum">
          {sessions.map((session) => (
            <li key={session.id}>
              <div className="class-template-curriculum__name">{session.name}</div>
              {session.description && <div className="class-template-curriculum__description">{session.description}</div>}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

CurriculumCard.propTypes = { sessions: PropTypes.array.isRequired };

function PageSectionsCard({ initialSections, canManage, updatePath, csrfToken }) {
  const [savedSections, setSavedSections] = useState(initialSections);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const startEditing = () => setDraft(sectionsFromTemplate(savedSections));

  const save = async () => {
    const message = sectionsError(draft);
    if (message) {
      toastError(message);
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(
        updatePath,
        { class_template: { class_template_sections_attributes: sectionsAttributes(draft, savedSections) } },
        { headers: { "X-CSRF-Token": csrfToken } }
      );
      setSavedSections(data.class_template_sections || []);
      setDraft(null);
      toastSuccess("Page sections saved.");
    } catch (e) {
      toastError(errorText(e, "Could not save page sections."));
    } finally {
      setSaving(false);
    }
  };

  const editing = draft !== null;
  const actions = canManage && (editing ? (
    <div className="d-flex classes-gap-8">
      <Button variant="secondary" onClick={() => setDraft(null)} disabled={saving}>Cancel</Button>
      <Button variant="primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save Sections"}</Button>
    </div>
  ) : (
    <Button variant="secondary" icon={Edit01} onClick={startEditing}>Edit Sections</Button>
  ));

  return (
    <Card
      header={(
        <CardHeader
          title="Page Sections"
          supportingText="Shown on every public class page from this template, below the description."
          actions={actions}
        />
      )}
    >
      {editing && <TemplateSectionsEditor sections={draft} onChange={setDraft} />}
      {!editing && savedSections.length === 0 && <div className="classes-text-secondary">No page sections yet.</div>}
      {!editing && savedSections.length > 0 && (
        <div className="class-template-sections">
          {savedSections.map((section) => (
            <div key={section.id} className="class-template-sections__item">
              <div className="class-template-curriculum__name">{section.heading}</div>
              {section.body ? (
                <div className="class-template-sections__body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.body) }} />
              ) : (
                <div className="classes-text-secondary small">No text yet, so it stays hidden on the class page.</div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

PageSectionsCard.propTypes = {
  initialSections: PropTypes.array.isRequired,
  canManage: PropTypes.bool,
  updatePath: PropTypes.string,
  csrfToken: PropTypes.string,
};

function DefaultsCard({ classTemplate, currencyCode }) {
  return (
    <Card header={<CardHeader title="Defaults" />}>
      <dl className="row mb-0">
        <DetailField label="Format" wide><ClassFormatBadge format={classTemplate.format} /></DetailField>
        <DetailField label="Default price" wide>
          {classTemplate.default_price != null ? formatPrice(Number(classTemplate.default_price), currencyCode) : null}
        </DetailField>
        <DetailField label="Default capacity" wide>
          {classTemplate.default_capacity ? `${classTemplate.default_capacity} spots` : null}
        </DetailField>
        <DetailField label="Default sessions" wide>
          {sessionLengthLabel(classTemplate.sessions_count, classTemplate.default_session_duration_minutes)}
        </DetailField>
        <DetailField label="Waitlist" wide>
          {classTemplate.default_waitlist_enabled ? `On · held ${classTemplate.default_waitlist_claim_window_label}` : "Off"}
        </DetailField>
        {classTemplate.internal_code && (
          <DetailField label="Internal code" wide><code className="class-template-code">{classTemplate.internal_code}</code></DetailField>
        )}
      </dl>
    </Card>
  );
}

DefaultsCard.propTypes = { classTemplate: PropTypes.object.isRequired, currencyCode: PropTypes.string };

export function interestListLabel(count) {
  if (count === 0) return "No one is waiting yet.";
  return `${count} ${count === 1 ? "person is" : "people are"} waiting to hear about the next session.`;
}

function InterestListCard({ count }) {
  return (
    <Card header={<CardHeader title="Interest List" supportingText="People who asked to hear about the next session from a sold-out class, or from a class whose registration hadn't opened yet." />}>
      <div className="class-template-interest" data-testid="class-template-interest-count">
        <div className="class-template-interest__count">{count}</div>
        <div>{interestListLabel(count)}</div>
      </div>
      <div className="classes-text-secondary small mt-2">They get one email when you publish the next publicly listed class from this template.</div>
    </Card>
  );
}

InterestListCard.propTypes = { count: PropTypes.number.isRequired };

function ClassTemplateShowApp(props) {
  const {
    classTemplate, canManage, editPath, updatePath, archivePath, unarchivePath, csrfToken, breadcrumb,
    currencyCode, classInstances, classInstancesCount, classesPath, newInstancePath, interestCount,
  } = props;
  const [working, setWorking] = useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const sessions = classTemplate.class_template_sessions || [];
  const canSchedule = canManage && !classTemplate.archived && !!newInstancePath;

  const toggleArchive = async () => {
    setWorking(true);
    try {
      await axios.post(classTemplate.archived ? unarchivePath : archivePath, {}, {
        headers: { "X-CSRF-Token": csrfToken },
      });
      window.location.reload();
    } catch (e) {
      setWorking(false);
      toastError("Could not update template.");
    }
  };

  const actions = canManage && (
    <div className="d-flex align-items-center flex-wrap classes-gap-8">
      <Button as="a" variant="secondary" icon={Edit01} href={editPath}>Edit Template</Button>
      {canSchedule && (
        <Button as="a" variant="primary" icon={Plus} href={newInstancePath}>Schedule Class</Button>
      )}
      <Menu
        placement="bottom-end"
        trigger={<Button variant="tertiary" icon={DotsVertical} aria-label="More template actions" disabled={working} />}
        items={[{ label: classTemplate.archived ? "Unarchive Template" : "Archive Template", icon: Archive, onClick: () => (classTemplate.archived ? toggleArchive() : setConfirmingArchive(true)) }]}
      />
    </div>
  );

  return (
    <DetailPageLayout
      breadcrumbs={breadcrumb}
      title={classTemplate.name}
      sectionTitle="Template details"
      badges={(
        <>
          <ClassCategoryBadge category={classTemplate.category} size="md" />
          <ClassFormatBadge format={classTemplate.format} size="md" />
          {classTemplate.archived && <Badge color="gray" size="md" dot>Archived</Badge>}
        </>
      )}
      supportingText={templateMeta(classTemplate, currencyCode)}
      actions={actions}
    >
      <DetailColumns rail={<><DefaultsCard classTemplate={classTemplate} currencyCode={currencyCode} /><InterestListCard count={interestCount} /></>}>
        <Card header={<CardHeader title="Image" />}>
          {classTemplate.main_image_url
            ? <img src={classTemplate.main_image_url} alt="Template image" className="classes-image-preview" />
            : <div className="classes-text-secondary">No image yet. New classes from this template start with the color gradient.</div>}
        </Card>
        <Card header={<CardHeader title="Description" />}>
          {classTemplate.description
            ? <div className="class-template-description">{classTemplate.description}</div>
            : <div className="classes-text-secondary">No description yet.</div>}
        </Card>
        <InstancesCard
          classInstances={classInstances}
          totalCount={classInstancesCount}
          classesPath={classesPath}
          newInstancePath={canSchedule ? newInstancePath : null}
        />
        <CurriculumCard sessions={sessions} />
        <PageSectionsCard
          initialSections={classTemplate.class_template_sections || []}
          canManage={canManage}
          updatePath={updatePath}
          csrfToken={csrfToken}
        />
      </DetailColumns>
      {confirmingArchive && (
        <ArchiveConfirmModal
          kind="template"
          name={classTemplate.name}
          working={working}
          onConfirm={toggleArchive}
          onClose={() => setConfirmingArchive(false)}
        />
      )}
      <ToastContainer icon={false} />
    </DetailPageLayout>
  );
}

ClassTemplateShowApp.propTypes = {
  classTemplate: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    internal_code: PropTypes.string,
    category: PropTypes.string,
    description: PropTypes.string,
    format: PropTypes.string,
    default_session_duration_minutes: PropTypes.number,
    default_capacity: PropTypes.number,
    default_price: PropTypes.string,
    default_waitlist_enabled: PropTypes.bool,
    default_waitlist_claim_window_hours: PropTypes.number,
    default_waitlist_claim_window_label: PropTypes.string,
    sessions_count: PropTypes.number,
    archived: PropTypes.bool,
    class_template_sessions: PropTypes.array,
    class_template_sections: PropTypes.array,
    main_image_url: PropTypes.string,
  }).isRequired,
  canManage: PropTypes.bool,
  editPath: PropTypes.string,
  updatePath: PropTypes.string,
  archivePath: PropTypes.string,
  unarchivePath: PropTypes.string,
  csrfToken: PropTypes.string,
  breadcrumb: PropTypes.array,
  currencyCode: PropTypes.string,
  classInstances: PropTypes.array,
  classInstancesCount: PropTypes.number.isRequired,
  classesPath: PropTypes.string,
  newInstancePath: PropTypes.string,
  interestCount: PropTypes.number.isRequired,
};

ClassTemplateShowApp.defaultProps = {
  currencyCode: "USD",
  classInstances: [],
  classesPath: "",
};

export default ClassTemplateShowApp;
